// Saturo grounded support chatbot widget.
// Talks to the saturo-chatbot backend (POST {api-url}/chat). The backend only
// answers from the curated knowledge base and falls back to support@saturo.com.

const STORAGE_KEY = 'saturo-chat-state-v1';

// Live-chat strings keyed by CONVERSATION language (detected by the backend
// per message). A customer writing German on the English page must get the
// handover in German too — page-locale strings alone can't do that.
const LIVE_STRINGS = {
  de: {
    human: 'Mit einer echten Person sprechen',
    agentLabel: 'Saturo Team',
    humanPending: 'Einen Moment — ich hole jemanden aus dem Team dazu. Du kannst währenddessen weiterschreiben.',
    humanOffline: 'Unser Team ist hier Mo–Fr von 9 bis 17 Uhr erreichbar. Gerade ist niemand da — schreib uns an support@saturo.com, wir melden uns so schnell wie möglich.',
    humanTimeout: 'Tut uns leid — gerade schafft es niemand aus dem Team rechtzeitig hierher. Schreib uns an support@saturo.com, wir antworten so schnell wie möglich. Du kannst auch einfach weiter mit mir chatten.',
    humanJoined: 'Du chattest jetzt mit dem Saturo Team.',
    agentGone: 'Das Team ist gerade nicht mehr am Chat — ich übernehme wieder. Frag mich einfach weiter, oder schreib uns an support@saturo.com, dann melden wir uns.',
    liveEnded: 'Live-Chat beendet — ich bin wieder für dich da.',
    orderNumPh: 'Bestellnummer, z. B. #12345',
    orderEmailPh: 'E-Mail-Adresse der Bestellung',
    orderCheck: 'Status prüfen',
    cartAdd: 'In den Warenkorb',
    cartAdded: '✓ Im Warenkorb',
    ticketEmailPh: 'Deine E-Mail-Adresse',
    ticketSend: 'Antwort per E-Mail erhalten',
    cartSoldOut: 'Diese Sorte ist leider gerade ausverkauft — probier eine andere oder schau in ein paar Tagen wieder vorbei.',
    feedbackUp: 'Antwort war hilfreich',
    feedbackDown: 'Antwort war nicht hilfreich',
    feedbackThanks: 'Danke fürs Feedback!',
    attach: 'Datei anhängen',
    fileTooLarge: 'Die Datei ist zu groß (max. 8 MB).',
    fileType: 'Dieses Dateiformat kann ich nicht annehmen — bitte ein Foto (JPG/PNG) oder ein PDF.',
    fileNoted: 'Datei angehängt — bei einer Übergabe ans Team oder einem Ticket wird sie mitgeschickt.',
  },
  en: {
    human: 'Talk to a real person',
    agentLabel: 'Saturo Team',
    humanPending: 'One moment — I’m getting someone from the team. You can keep typing in the meantime.',
    humanOffline: 'Our team is available here Mon–Fri, 9–17 (CET). Right now nobody is in — email us at support@saturo.com and we’ll get back to you as soon as possible.',
    humanTimeout: 'Sorry — nobody from the team could make it in time. Email us at support@saturo.com and we’ll reply as soon as we can. You can also keep chatting with me.',
    humanJoined: 'You’re now chatting with the Saturo team.',
    agentGone: 'Looks like the team got pulled away — I’ll take over again. Feel free to keep asking me, or email support@saturo.com and we’ll follow up.',
    liveEnded: 'Live chat ended — I’m here again if you need anything.',
    orderNumPh: 'Order number, e.g. #12345',
    orderEmailPh: 'E-mail address of the order',
    orderCheck: 'Check status',
    cartAdd: 'Add to cart',
    cartAdded: '✓ Added to cart',
    ticketEmailPh: 'Your e-mail address',
    ticketSend: 'Get a reply by e-mail',
    cartSoldOut: 'That flavor just sold out — try another one or check back in a few days.',
    feedbackUp: 'Helpful answer',
    feedbackDown: 'Not helpful',
    feedbackThanks: 'Thanks for the feedback!',
    attach: 'Attach a file',
    fileTooLarge: 'That file is too large (max. 8 MB).',
    fileType: 'I can’t accept that file format — please send a photo (JPG/PNG) or a PDF.',
    fileNoted: 'File attached — it will be included when the team takes over or a ticket is created.',
  },
};

class SaturoChatbot extends HTMLElement {
  connectedCallback() {
    if (this.dataset.initialized) return;
    this.dataset.initialized = 'true';

    this.apiUrl = (this.getAttribute('api-url') || '').replace(/\/+$/, '');
    this.locale = (this.getAttribute('locale') || 'de').slice(0, 2);
    this.lang = this.locale === 'de' ? 'de' : 'en';
    try {
      this.strings = JSON.parse(this.getAttribute('data-strings') || '{}');
    } catch {
      this.strings = {};
    }
    this.busy = false;
    this.live = null; // null | 'pending' | 'active'
    this.state = this.loadState();

    this.render();
    for (const m of this.state.history) {
      // Upload-Marker als Bild/PDF-Chip wiederherstellen, nicht als Rohtext
      const up = m.role === 'user' && /^\[Datei vom Kunden: (https?:\/\/\S+?)\]$/.exec(m.content);
      if (up) this.appendUpload(up[1], up[1].endsWith('.pdf') ? 'application/pdf' : 'image/*');
      else this.appendMessage(m.role, m.content);
    }
    if (this.state.history.length === 0) {
      this.appendMessage('assistant', this.t('greeting'));
    }
    this.initLiveChat();
    this.checkEnabled();
  }

  // Upload a photo/screenshot: store it via the backend, show it inline and
  // hand the link to whoever is (or will be) helping — Slack thread in live
  // mode, otherwise it rides along in the transcript into tickets/handovers.
  async uploadFile(file) {
    if (!this.apiUrl) return;
    if (file.size > 8 * 1024 * 1024) {
      this.appendMessage('assistant', this.liveT('fileTooLarge'));
      return;
    }
    // Sofort eine lokale Vorschau zeigen (ausgegraut bis der Upload fertig
    // ist) — Tipp-Punkte sähen aus, als würde gleich eine Antwort kommen.
    const localUrl = URL.createObjectURL(file);
    const bubble = this.appendUpload(localUrl, file.type);
    bubble.classList.add('sc-upload--pending');
    try {
      const res = await fetch(`${this.apiUrl}/upload?session=${encodeURIComponent(this.state.sessionId)}`, {
        method: 'POST',
        headers: { 'Content-Type': file.type || 'application/octet-stream' },
        body: file,
      });
      const data = await res.json();
      if (!res.ok || !data.url) {
        bubble.remove();
        URL.revokeObjectURL(localUrl);
        this.appendMessage('assistant', data.error === 'unsupported_type' ? this.liveT('fileType') : this.t('error'));
        return;
      }
      bubble.classList.remove('sc-upload--pending');
      const img = bubble.querySelector('img');
      if (img) {
        img.addEventListener('load', () => URL.revokeObjectURL(localUrl), { once: true });
        img.src = data.url;
      }
      const link = bubble.querySelector('a');
      if (link) { link.href = data.url; URL.revokeObjectURL(localUrl); }
      this.state.history.push({ role: 'user', content: `[Datei vom Kunden: ${data.url}]` });
      this.state.history = this.state.history.slice(-20);
      this.saveState();
      if (this.live) {
        await fetch(`${this.apiUrl}/live/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: this.state.sessionId, message: `[Datei] ${data.url}` }),
        });
      } else {
        // Bot-Modus: der Bot sieht sich das Bild an und reagiert inhaltlich
        await this.botFollowUp(`[Datei vom Kunden: ${data.url}]`);
      }
    } catch (err) {
      bubble.remove();
      URL.revokeObjectURL(localUrl);
      this.appendMessage('assistant', this.t('error'));
      console.error('saturo-chatbot upload:', err);
    }
  }

  // Nach einem Upload: Marker an /chat schicken, damit der Bot das Bild
  // ansieht und darauf antwortet. Fallback: neutrale "Datei angehängt"-Notiz.
  async botFollowUp(marker) {
    const typing = this.showTyping();
    try {
      const res = await fetch(`${this.apiUrl}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: marker,
          history: this.chatHistory().filter((m) => m.content !== marker).slice(-8),
          sessionId: this.state.sessionId,
          lang: this.state.convLang || this.lang,
        }),
      });
      const data = await res.json();
      typing.remove();
      if (res.ok && data.reply) {
        this.appendMessage('assistant', data.reply);
        this.state.history.push({ role: 'assistant', content: data.reply });
        this.state.history = this.state.history.slice(-20);
        this.saveState();
      } else {
        this.appendSystem(this.liveT('fileNoted'));
      }
    } catch {
      typing.remove();
      this.appendSystem(this.liveT('fileNoted'));
    }
  }

  appendUpload(fileUrl, type) {
    const div = document.createElement('div');
    div.className = 'sc-msg sc-msg--user sc-upload';
    if (type === 'application/pdf') {
      const a = document.createElement('a');
      a.href = fileUrl;
      a.target = '_blank';
      a.rel = 'noopener';
      a.textContent = 'PDF';
      div.appendChild(a);
    } else {
      const img = document.createElement('img');
      img.src = fileUrl;
      img.alt = '';
      img.loading = 'lazy';
      div.appendChild(img);
    }
    this.messagesEl.appendChild(div);
    this.scrollDown();
    return div;
  }

  // Manual touch scrolling for the messages list. Needed because a stuck
  // top-layer overlay (the cookie-consent dialog keeps an invisible modal
  // open) can make the whole page inert on iOS: taps get forwarded by the
  // consent tool, but native pan gestures inside the chat die. Touch events
  // still bubble to the document, so we read the coordinates there and
  // scroll ourselves — but ONLY when the hit test shows the touch did not
  // reach the widget (healthy pages keep native scrolling, no double-scroll).
  bindScrollShim() {
    if (this.shimBound) return;
    this.shimBound = true;
    let active = false;
    let lastY = 0;
    document.addEventListener(
      'touchstart',
      (e) => {
        active = false;
        if (!this.hasAttribute('open')) return;
        const t = e.touches[0];
        if (!t) return;
        const r = this.messagesEl.getBoundingClientRect();
        if (t.clientX < r.left || t.clientX > r.right || t.clientY < r.top || t.clientY > r.bottom) return;
        const hit = document.elementFromPoint(t.clientX, t.clientY);
        active = hit ? !this.contains(hit) : true;
        if (active) {
          this.messagesEl.style.scrollBehavior = 'auto';
          lastY = t.clientY;
        }
      },
      { capture: true, passive: true }
    );
    document.addEventListener(
      'touchmove',
      (e) => {
        if (!active) return;
        const t = e.touches[0];
        if (!t) return;
        this.messagesEl.scrollTop += lastY - t.clientY;
        lastY = t.clientY;
      },
      { capture: true, passive: true }
    );
  }

  // Master switch from the backend settings: off → the widget disappears
  // entirely. Unreachable config fails open (bot stays up).
  async checkEnabled() {
    if (!this.apiUrl) return;
    try {
      const cfg = await fetch(`${this.apiUrl}/widget-config`).then((r) => r.json());
      if (cfg.enabled === false) {
        clearInterval(this.pollTimer);
        this.innerHTML = '';
      }
    } catch {
      /* fail open */
    }
  }

  // There is no permanent button: the bot answers first and only offers the
  // handover when the backend says so (offerHuman). Here we resume a relay
  // that survived a page navigation.
  async initLiveChat() {
    if (!this.apiUrl) return;
    if (this.state.live) {
      this.live = 'pending';
      // A resumed relay can hit a timeout and offer a ticket without the
      // customer ever opening the panel on this page, so it needs the flag now.
      this.loadTicketStatus();
      this.startPolling();
    }
  }

  // Whether the backend can turn a missed chat into a ticket. Only ever read
  // by appendTicketForm(), deep inside a conversation — so it is fetched on
  // first open instead of on every page view.
  async loadTicketStatus() {
    if (!this.apiUrl || this.ticketStatusRequested) return;
    this.ticketStatusRequested = true;
    try {
      const status = await fetch(`${this.apiUrl}/live/status`).then((r) => r.json());
      this.ticketsEnabled = Boolean(status.tickets);
    } catch {
      this.ticketsEnabled = false;
    }
  }

  // Missed-chat ticket form: e-mail in, Zoho ticket with transcript out.
  appendTicketForm(reason) {
    if (!this.ticketsEnabled) return;
    const form = document.createElement('form');
    form.className = 'sc-orderform sc-ticketform';
    form.innerHTML = `
      <input type="email" name="email" autocomplete="email" placeholder="${esc(this.liveT('ticketEmailPh'))}" required maxlength="120">
      <button type="submit">${esc(this.liveT('ticketSend'))}</button>
    `;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('button');
      btn.disabled = true;
      try {
        const res = await fetch(`${this.apiUrl}/ticket`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            email: form.email.value.trim(),
            sessionId: this.state.sessionId,
            reason,
            history: this.state.history.slice(-20),
            pageUrl: location.href,
            lang: this.state.convLang || this.lang,
          }),
        });
        const data = await res.json();
        this.appendMessage('assistant', data.reply);
        this.state.history.push({ role: 'assistant', content: data.reply });
        this.saveState();
        if (data.ok) form.remove();
        else btn.disabled = false;
      } catch {
        this.appendMessage('assistant', this.t('error'));
        btn.disabled = false;
      }
    });
    this.messagesEl.appendChild(form);
    this.scrollDown();
  }

  // Daumen unter einer Bot-Antwort: ein Klick pro Antwort, keine Chat-Zeile
  // als Reaktion (nur ein kurzer Dank an Ort und Stelle). Das Signal landet
  // im Transkript, damit sich schwache Antworten in der Inbox finden lassen.
  // Nur nach echten /chat-Antworten — nicht unter Begrüßung, Fehlermeldung
  // oder Nachrichten aus dem Livechat.
  appendFeedback(question, answer) {
    const row = document.createElement('div');
    row.className = 'sc-fb';
    const icon =
      '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true">' +
      '<path d="M7 21V10l4-7a2 2 0 0 1 3.7 1.4L14 9h5a2 2 0 0 1 2 2.4l-1.4 7A2.5 2.5 0 0 1 17 21H7zM7 21H4.5A1.5 1.5 0 0 1 3 19.5v-8A1.5 1.5 0 0 1 4.5 10H7" stroke-linecap="round" stroke-linejoin="round"/></svg>';
    row.innerHTML = ['up', 'down']
      .map((dir) => {
        const label = esc(this.liveT(dir === 'up' ? 'feedbackUp' : 'feedbackDown'));
        return `<button type="button" data-dir="${dir}" aria-label="${label}" title="${label}">${icon}</button>`;
      })
      .join('');
    row.addEventListener('click', (e) => {
      const btn = e.target.closest('button');
      if (!btn || row.dataset.done) return;
      row.dataset.done = '1';
      btn.classList.add('sel');
      row.querySelectorAll('button').forEach((b) => {
        b.disabled = true;
      });
      const thanks = document.createElement('span');
      thanks.textContent = this.liveT('feedbackThanks');
      row.appendChild(thanks);
      this.reportFeedback(btn.dataset.dir, question, answer);
    });
    this.messagesEl.appendChild(row);
    this.scrollDown();
  }

  reportFeedback(value, question, answer) {
    fetch(`${this.apiUrl}/feedback`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({ sessionId: this.state.sessionId, value, question, answer }),
    }).catch(() => {});
  }

  // The backend offered a human — show a one-shot inline button under the reply.
  appendHumanOffer() {
    const div = document.createElement('div');
    div.className = 'sc-offer';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'sc-human';
    btn.textContent = this.liveT('human');
    btn.addEventListener('click', () => {
      btn.disabled = true;
      this.startHuman();
    });
    div.appendChild(btn);
    this.messagesEl.appendChild(div);
    this.scrollDown();
  }

  // Add-to-cart card under product answers. Cart calls only work on the
  // storefront origin — anywhere else (dev harness) the card is skipped.
  // Availability is re-checked LIVE against the storefront so a stale backend
  // payload can never offer a sold-out variant; fully sold out → no card.
  async appendCartCard(product) {
    if (!/(saturo\.com|myshopify\.com|shopifypreview\.com)$/.test(location.hostname)) return;
    if (!product.variants?.length) return;
    try {
      const live = await fetch(`/products/${encodeURIComponent(product.handle)}.js`).then((r) => r.json());
      const availableIds = new Set(live.variants.filter((v) => v.available).map((v) => String(v.id)));
      product = { ...product, variants: product.variants.filter((v) => availableIds.has(String(v.id))) };
    } catch {
      /* storefront JSON unreachable → trust the backend payload */
    }
    if (!product.variants.length) return;
    const card = document.createElement('div');
    card.className = 'sc-cart';
    const price = product.variants[0].price;
    // Shopify CDN can resize on the fly — request a small square thumb.
    const thumb = product.image ? `${product.image}${product.image.includes('?') ? '&' : '?'}width=160` : null;
    card.innerHTML = `
      ${thumb ? `<a href="${esc(product.url)}" class="sc-cart-img"><img src="${esc(thumb)}" alt="${esc(product.title)}" loading="lazy" width="72" height="72"></a>` : ''}
      <div class="sc-cart-body">
      <div class="sc-cart-head">
        <strong>${esc(product.title)}</strong>
        <span>€ ${esc(price)}</span>
      </div>
      ${product.variants.length > 1
        ? `<select>${product.variants.map((v) => `<option value="${esc(v.id)}">${esc(v.flavor)}</option>`).join('')}</select>`
        : `<input type="hidden" value="${esc(product.variants[0].id)}">`}
      <button type="button">${esc(this.liveT('cartAdd'))}</button>
      </div>
    `;
    // flavor change swaps the packshot (tiny CDN thumbs, fetched on demand)
    const select = card.querySelector('select');
    const img = card.querySelector('.sc-cart-img img');
    const priceEl = card.querySelector('.sc-cart-head span');
    if (select) {
      const thumbOf = (u) => `${u}${u.includes('?') ? '&' : '?'}width=160`;
      select.addEventListener('change', () => {
        const v = product.variants.find((x) => String(x.id) === select.value);
        if (img) {
          if (v?.image) img.src = thumbOf(v.image);
          else if (product.image) img.src = thumbOf(product.image);
        }
        // Sorten sind heute gleich teuer, aber sobald eine einen eigenen
        // (Aktions-)Preis bekommt, stand hier sonst der Preis der ersten.
        if (priceEl && v?.price) priceEl.textContent = `€ ${v.price}`;
      });
    }
    const btn = card.querySelector('button');
    // Die Karte bleibt nach dem Hinzufügen benutzbar: die Bestätigung steht
    // kurz im Button, dann steht er wieder auf "In den Warenkorb" — so lässt
    // sich nachlegen oder eine zweite Sorte wählen. Ein Sortenwechsel setzt
    // ihn sofort zurück (die Bestätigung galt ja der vorigen Sorte).
    let addedTimer = null;
    const resetBtn = () => {
      clearTimeout(addedTimer);
      addedTimer = null;
      btn.textContent = this.liveT('cartAdd');
      btn.disabled = false;
    };
    select?.addEventListener('change', resetBtn);
    btn.addEventListener('click', async () => {
      clearTimeout(addedTimer);
      btn.disabled = true;
      try {
        const variantId = String(card.querySelector('select, input').value);
        await this.addToCart(variantId);
        btn.textContent = this.liveT('cartAdded');
        addedTimer = setTimeout(resetBtn, 2000);
        this.reportCartAdd(product, variantId);
      } catch (err) {
        btn.disabled = false;
        this.appendMessage('assistant', err?.soldOut ? this.liveT('cartSoldOut') : this.t('error'));
        console.error('saturo-chatbot cart:', err);
      }
    });
    this.messagesEl.appendChild(card);
    this.scrollDown();
  }

  // Kaufzuordnung: das Cart-Attribut wandert mit in die Bestellung und ist
  // im Shopify-Admin unter "Zusätzliche Details" sichtbar — damit lässt sich
  // Umsatz aus dem Chat auf die Session zurückführen. /cart/update.js merged
  // Attribute, andere Cart-Attribute bleiben also unangetastet. Fehler sind
  // egal: der Artikel liegt bereits im Warenkorb.
  tagCart() {
    const routes = window.Theme?.routes ?? {};
    fetch(routes.cart_update_url ?? '/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({
        attributes: { chatbot: 'yes', chatbot_session: this.state.sessionId ?? '' },
      }),
    }).catch(() => {});
  }

  // Notiz ans Backend, damit die Chat-Inbox zeigt, welche Gespräche zu einem
  // Warenkorb geführt haben. Fire-and-forget; keepalive, weil der Klick oft
  // direkt in den Warenkorb-Drawer oder zum Checkout führt.
  reportCartAdd(product, variantId) {
    const variant = product.variants?.find((v) => String(v.id) === String(variantId));
    fetch(`${this.apiUrl}/cart-event`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      keepalive: true,
      body: JSON.stringify({
        sessionId: this.state.sessionId,
        title: product.title,
        flavor: variant?.flavor ?? '',
        variantId: String(variantId),
      }),
    }).catch(() => {});
  }

  // Same mechanics as the theme's product-form-component (used by the
  // homepage carousel quick add): FormData against cart_add_url plus the
  // CartLinesUpdateEvent with a deferred promise, so the theme's cart drawer
  // and bubble update exactly like a native add to cart.
  async addToCart(variantId) {
    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', '1');
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) formData.append('sections', item.dataset.sectionId);
    });

    let events = null;
    let deferred = null;
    try {
      events = await import('@shopify/events');
      deferred = events.CartLinesUpdateEvent.createPromise();
      this.dispatchEvent(
        new events.CartLinesUpdateEvent({
          action: 'add',
          context: 'product',
          lines: [{ merchandiseId: variantId, quantity: 1 }],
          promise: deferred.promise,
        })
      );
    } catch {
      /* theme event module unavailable (dev harness) — plain add still works */
    }

    const routes = window.Theme?.routes ?? {};
    const res = await fetch(routes.cart_add_url ?? '/cart/add.js', {
      method: 'POST',
      headers: { Accept: 'application/json' },
      body: formData,
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok || data.status) {
      const err = new Error(data.message || 'cart add failed');
      // 422 = variant not sellable (sold out / delisted) → friendly message
      err.soldOut = res.status === 422 || data.status === 422;
      deferred?.reject?.(err);
      throw err;
    }

    this.tagCart();

    if (deferred && events) {
      try {
        const cart = await fetch(routes.cart_url ? `${routes.cart_url}.js` : '/cart.js').then((r) => r.json());
        deferred.resolve({
          cart: events.CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: {
            didError: false,
            items: cart.items,
            source: 'chat-widget',
            sourceId: 'saturo-chatbot',
            itemCount: 1,
          },
        });
      } catch {
        deferred.resolve({ detail: { didError: false, items: [], source: 'chat-widget', sourceId: 'saturo-chatbot', itemCount: 1 } });
      }
    }
  }

  // Inline order-lookup form (order number + e-mail). The e-mail never enters
  // the visible chat history — only the backend sees it, and it isn't logged.
  appendOrderForm() {
    const form = document.createElement('form');
    form.className = 'sc-orderform';
    form.innerHTML = `
      <input type="text" name="orderNumber" inputmode="text" autocomplete="off" placeholder="${esc(this.liveT('orderNumPh'))}" required maxlength="20">
      <input type="email" name="email" autocomplete="email" placeholder="${esc(this.liveT('orderEmailPh'))}" required maxlength="120">
      <button type="submit">${esc(this.liveT('orderCheck'))}</button>
    `;
    form.addEventListener('submit', async (e) => {
      e.preventDefault();
      const btn = form.querySelector('button');
      btn.disabled = true;
      const typing = this.showTyping();
      try {
        const res = await fetch(`${this.apiUrl}/order-status`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            orderNumber: form.orderNumber.value.trim(),
            email: form.email.value.trim(),
            sessionId: this.state.sessionId,
            lang: this.state.convLang || this.lang,
          }),
        });
        const data = await res.json();
        typing.remove();
        if (!res.ok || !data.reply) throw new Error(data.error || 'bad response');
        this.appendMessage('assistant', data.reply);
        this.state.history.push({ role: 'assistant', content: data.reply });
        this.state.history = this.state.history.slice(-20);
        this.saveState();
        if (data.found) {
          form.remove();
        } else {
          btn.disabled = false; // let them correct a typo
        }
      } catch {
        typing.remove();
        this.appendMessage('assistant', this.t('error'));
        btn.disabled = false;
      }
    });
    this.messagesEl.appendChild(form);
    this.scrollDown();
    form.orderNumber.focus();
  }

  async startHuman() {
    if (this.live || !this.apiUrl) return;
    try {
      const res = await fetch(`${this.apiUrl}/live/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.state.sessionId,
          history: this.chatHistory().slice(-10),
          pageUrl: location.href,
        }),
      });
      const data = await res.json();
      if (data.status === 'pending' || data.status === 'active') {
        this.live = data.status;
        this.state.live = true;
        this.saveState();
        this.appendMessage('assistant', this.liveT('humanPending'));
        this.startPolling();
      } else {
        this.appendMessage('assistant', this.liveT('humanOffline'));
        this.appendTicketForm('offline');
      }
    } catch {
      this.appendMessage('assistant', this.t('error'));
    }
  }

  startPolling() {
    clearInterval(this.pollTimer);
    this.pollTimer = setInterval(() => this.pollLive(), 3000);
  }

  async pollLive() {
    try {
      const res = await fetch(`${this.apiUrl}/live/poll?sessionId=${encodeURIComponent(this.state.sessionId)}`);
      const data = await res.json();
      for (const m of data.messages ?? []) {
        this.appendMessage('agent', m.content);
        this.state.history.push({ role: 'agent', content: m.content });
      }
      if (data.messages?.length) {
        if (this.live !== 'active') this.appendSystem(this.liveT('humanJoined'));
        this.live = 'active';
        this.state.history = this.state.history.slice(-20);
        this.saveState();
      }
      if (data.status === 'timeout' || data.status === 'gone') {
        if (this.live !== 'active' || data.status === 'gone') {
          if (data.status === 'timeout') {
            this.appendMessage('assistant', this.liveT('humanTimeout'));
            this.appendTicketForm('timeout');
          }
          this.liveOff();
        }
      }
      if (data.status === 'agent_stalled') {
        this.appendMessage('assistant', this.liveT('agentGone'));
        this.appendTicketForm('agent_gone');
        this.liveOff();
      }
      if (data.status === 'closed_idle') {
        this.appendSystem(this.liveT('liveEnded'));
        this.liveOff();
      }
    } catch {
      /* transient poll errors: keep trying until timeout status arrives */
    }
  }

  liveOff() {
    clearInterval(this.pollTimer);
    this.pollTimer = null;
    this.live = null;
    this.state.live = false;
    this.saveState();
  }

  chatHistory() {
    // agent turns count as assistant context for the bot
    return this.state.history.map(({ role, content }) => ({
      role: role === 'user' ? 'user' : 'assistant',
      content,
    }));
  }

  t(key) {
    const fallback = {
      title: 'Saturo Support',
      subtitle: 'Answers from our knowledge base',
      greeting: 'Hi! Ask me anything about Saturo products, shipping or your order.',
      placeholder: 'Type your question…',
      send: 'Send',
      close: 'Close chat',
      open: 'Chat',
      error: 'Something went wrong. Please try again or email support@saturo.com.',
      disclaimer: 'Automated assistant — answers come from the Saturo knowledge base.',
      human: 'Talk to a real person',
      agentLabel: 'Saturo Team',
      humanPending: 'One moment — I’m getting someone from the team. You can keep typing in the meantime.',
      humanOffline: 'Our team is available here Mon–Fri, 9–17 (CET). Right now nobody is in — email us at support@saturo.com and we’ll get back to you as soon as possible.',
      humanTimeout: 'Sorry — nobody from the team could make it in time. Email us at support@saturo.com and we’ll reply as soon as we can. You can also keep chatting with me.',
      humanJoined: 'You’re now chatting with the Saturo team.',
      agentGone: 'Looks like the team got pulled away — I’ll take over again. Feel free to keep asking me, or email support@saturo.com and we’ll follow up.',
      liveEnded: 'Live chat ended — I’m here again if you need anything.',
      orderNumPh: 'Order number, e.g. #12345',
      orderEmailPh: 'E-mail address of the order',
      orderCheck: 'Check status',
      cartAdd: 'Add to cart',
      cartAdded: '✓ Added to cart',
      ticketEmailPh: 'Your e-mail address',
      ticketSend: 'Get a reply by e-mail',
      cartSoldOut: 'That flavor just sold out — try another one or check back in a few days.',
      feedbackUp: 'Helpful answer',
      feedbackDown: 'Not helpful',
      feedbackThanks: 'Thanks for the feedback!',
      attach: 'Attach a file',
      fileTooLarge: 'That file is too large (max. 8 MB).',
      fileType: 'I can’t accept that file format — please send a photo (JPG/PNG) or a PDF.',
      fileNoted: 'File attached — it will be included when the team takes over or a ticket is created.',
    };
    return this.strings[key] || fallback[key];
  }

  // Live-chat strings in the conversation language (from the backend's
  // per-message detection); page-locale strings when they agree or for 'cs'.
  liveT(key) {
    const conv = this.state.convLang;
    if (conv && conv !== this.lang && LIVE_STRINGS[conv]?.[key]) return LIVE_STRINGS[conv][key];
    // Sonst zaehlt die Seitensprache: erst die vom Theme gelieferten Strings,
    // dann die Seitensprache aus LIVE_STRINGS. Ohne den zweiten Schritt landen
    // neue Keys, die das Snippet noch nicht kennt, im englischen t()-Fallback
    // — auf einer deutschen Seite mitten im deutschen Gespraech.
    return this.strings[key] ?? LIVE_STRINGS[this.lang]?.[key] ?? this.t(key);
  }

  loadState() {
    try {
      const raw = sessionStorage.getItem(STORAGE_KEY);
      if (raw) {
        const s = JSON.parse(raw);
        if (Array.isArray(s.history)) {
          return { sessionId: s.sessionId, history: s.history.slice(-20), live: s.live === true, convLang: s.convLang };
        }
      }
    } catch {
      /* private mode etc. */
    }
    return { sessionId: crypto.randomUUID(), history: [] };
  }

  saveState() {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(this.state));
    } catch {
      /* ignore */
    }
  }

  render() {
    this.innerHTML = `
      <button type="button" class="sc-fab" aria-label="${esc(this.t('open'))}">
        <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 3C6.5 3 2 6.9 2 11.7c0 2.7 1.4 5.1 3.7 6.7-.2.9-.7 2.2-1.6 3.2 0 0 2.8-.4 4.8-1.6 1 .3 2 .4 3.1.4 5.5 0 10-3.9 10-8.7S17.5 3 12 3z"/></svg>
        <span>${esc(this.t('open'))}</span>
      </button>
      <section class="sc-panel" role="dialog" aria-label="${esc(this.t('title'))}">
        <header class="sc-header">
          <div>
            <strong>${esc(this.t('title'))}</strong>
            <span class="sc-sub">${esc(this.t('subtitle'))}</span>
          </div>
          <button type="button" class="sc-close" aria-label="${esc(this.t('close'))}">&times;</button>
        </header>
        <div class="sc-messages" aria-live="polite"></div>
        <form class="sc-form">
          <button type="button" class="sc-attach" aria-label="${esc(this.t('attach'))}" title="${esc(this.t('attach'))}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true"><path d="M21 12.5 12.6 20.9a5.5 5.5 0 0 1-7.8-7.8l8.8-8.8a3.7 3.7 0 0 1 5.2 5.2l-8.8 8.8a1.85 1.85 0 0 1-2.6-2.6l8-8" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
          <input type="file" class="sc-file" accept="image/jpeg,image/png,image/webp,image/gif,image/heic,application/pdf" hidden>
          <textarea class="sc-input" rows="1" maxlength="2000" placeholder="${esc(this.t('placeholder'))}" required></textarea>
          <button type="submit" class="sc-send" aria-label="${esc(this.t('send'))}" title="${esc(this.t('send'))}">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.2" aria-hidden="true"><path d="M12 19V5m0 0-6 6m6-6 6 6" stroke-linecap="round" stroke-linejoin="round"/></svg>
          </button>
        </form>
        <div class="sc-disclaimer">${esc(this.t('disclaimer'))}</div>
      </section>
    `;

    this.messagesEl = this.querySelector('.sc-messages');
    this.inputEl = this.querySelector('.sc-input');
    this.sendEl = this.querySelector('.sc-send');
    this.panelEl = this.querySelector('.sc-panel');

    this.querySelector('.sc-fab').addEventListener('click', () => this.open());
    this.querySelector('.sc-close').addEventListener('click', () => this.close());
    this.querySelector('.sc-form').addEventListener('submit', (e) => {
      e.preventDefault();
      this.send();
    });
    this.fileEl = this.querySelector('.sc-file');
    this.querySelector('.sc-attach').addEventListener('click', () => this.fileEl.click());
    this.fileEl.addEventListener('change', () => {
      const file = this.fileEl.files?.[0];
      this.fileEl.value = '';
      if (file) this.uploadFile(file);
    });
    this.inputEl.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.send();
      }
      if (e.key === 'Escape') this.close();
    });
  }

  open() {
    this.setAttribute('open', '');
    this.loadTicketStatus();
    this.bindScrollShim();
    // Autofocus only on desktop: on phones it pops the keyboard immediately
    // and iOS scrolls to the input, pushing the greeting off screen.
    if (window.matchMedia('(min-width: 481px)').matches) {
      this.inputEl.focus();
    }
    this.glueToViewport();
    this.lockPage(true);
    this.scrollDown();
  }

  // While the fullscreen chat is open on phones, freeze the page behind it
  // (the chatbot.com/Intercom recipe): Safari can then never scroll the site
  // into view under the panel or beside the keyboard.
  lockPage(on) {
    if (!window.matchMedia('(max-width: 480px)').matches) return;
    const body = document.body;
    if (on) {
      this.savedScrollY = window.scrollY;
      body.style.position = 'fixed';
      body.style.top = `-${this.savedScrollY}px`;
      body.style.left = '0';
      body.style.right = '0';
      body.style.width = '100%';
    } else {
      body.style.position = '';
      body.style.top = '';
      body.style.left = '';
      body.style.right = '';
      body.style.width = '';
      window.scrollTo(0, this.savedScrollY ?? 0);
    }
  }

  // iOS: the keyboard shrinks only the VISUAL viewport — a 100dvh panel
  // keeps its size, Safari scrolls, and the page shows through underneath.
  // Glue the fullscreen panel to the visual viewport instead.
  glueToViewport() {
    if (this.glueBound || !window.visualViewport) return;
    this.glueBound = true;
    const vv = window.visualViewport;
    const apply = () => {
      if (!this.hasAttribute('open') || !window.matchMedia('(max-width: 480px)').matches) {
        this.panelEl.style.top = '';
        this.panelEl.style.height = '';
        return;
      }
      this.panelEl.style.top = `${vv.offsetTop}px`;
      this.panelEl.style.height = `${vv.height}px`;
      this.scrollDown();
    };
    vv.addEventListener('resize', apply);
    vv.addEventListener('scroll', apply);
    apply();
  }

  close() {
    this.removeAttribute('open');
    this.lockPage(false);
    if (this.panelEl) {
      this.panelEl.style.top = '';
      this.panelEl.style.height = '';
    }
  }

  appendMessage(role, content) {
    const div = document.createElement('div');
    const kind = role === 'user' ? 'user' : role === 'agent' ? 'agent' : 'bot';
    div.className = `sc-msg sc-msg--${kind}`;
    div.innerHTML =
      (role === 'agent' ? `<span class="sc-agent-label">${esc(this.liveT('agentLabel'))}</span>` : '') +
      linkify(content);
    this.messagesEl.appendChild(div);
    this.scrollDown();
  }

  appendSystem(text) {
    const div = document.createElement('div');
    div.className = 'sc-system';
    div.textContent = text;
    this.messagesEl.appendChild(div);
    this.scrollDown();
  }

  showTyping() {
    const el = document.createElement('div');
    el.className = 'sc-typing';
    el.innerHTML = '<span></span><span></span><span></span>';
    this.messagesEl.appendChild(el);
    this.scrollDown();
    return el;
  }

  scrollDown() {
    requestAnimationFrame(() => {
      this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
    });
  }

  async send() {
    const message = this.inputEl.value.trim();
    if (!message || this.busy || !this.apiUrl) return;
    this.busy = true;
    this.sendEl.disabled = true;
    this.inputEl.value = '';

    this.appendMessage('user', message);
    this.state.history.push({ role: 'user', content: message });

    if (this.live) {
      // Live relay: the message goes to the Slack thread, replies arrive
      // through polling — no typing indicator, humans are slower than bots.
      try {
        const res = await fetch(`${this.apiUrl}/live/send`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ sessionId: this.state.sessionId, message }),
        });
        const data = await res.json();
        if (data.status === 'gone') {
          this.appendMessage('assistant', this.liveT('humanTimeout'));
          this.liveOff();
        }
      } catch {
        this.appendMessage('assistant', this.t('error'));
      }
      this.state.history = this.state.history.slice(-20);
      this.saveState();
      this.busy = false;
      this.sendEl.disabled = false;
      this.inputEl.focus();
      return;
    }

    const typing = this.showTyping();
    try {
      const res = await fetch(`${this.apiUrl}/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message,
          sessionId: this.state.sessionId,
          history: this.chatHistory().slice(-8, -1),
          // sticky conversation language: ambiguous messages keep the last
          // clearly detected language instead of falling back to page locale
          lang: this.state.convLang || this.lang,
        }),
      });
      const data = await res.json();
      typing.remove();
      if (!res.ok || !data.reply) throw new Error(data.error || 'bad response');

      if (data.lang) {
        this.state.convLang = data.lang;
      }
      this.appendMessage('assistant', data.reply);
      this.appendFeedback(message, data.reply);
      // startHuman: der Kunde hat die Uebergabe schon bejaht ("Ja" auf
      // "Soll ich jemanden holen?") — direkt starten, kein zweiter Klick.
      // offerHuman allein bleibt das Angebot mit Button.
      if (data.startHuman) this.startHuman();
      else if (data.offerHuman) this.appendHumanOffer();
      if (data.orderForm) this.appendOrderForm();
      if (data.product) this.appendCartCard(data.product);
      this.state.history.push({ role: 'assistant', content: data.reply });
      this.state.history = this.state.history.slice(-20);
      this.saveState();
    } catch (err) {
      typing.remove();
      this.appendMessage('assistant', this.t('error'));
      console.error('saturo-chatbot:', err);
    } finally {
      this.busy = false;
      this.sendEl.disabled = false;
      this.inputEl.focus();
    }
  }
}

function esc(s) {
  return String(s ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;');
}

// Escape everything, then turn plain URLs and e-mail addresses into links.
// The backend answers in plain text, but if Markdown bold slips through we
// render it instead of showing literal asterisks.
function linkify(text) {
  return esc(text)
    .replace(
      /(https?:\/\/[^\s<)]+)/g,
      '<a href="$1" target="_blank" rel="noopener">$1</a>'
    )
    .replace(
      /(^|\s)([a-z0-9._%+-]+@[a-z0-9.-]+\.[a-z]{2,})/gi,
      '$1<a href="mailto:$2">$2</a>'
    )
    .replace(/\*\*([^*\n]+)\*\*/g, '<strong>$1</strong>');
}

if (!customElements.get('saturo-chatbot')) {
  customElements.define('saturo-chatbot', SaturoChatbot);
}
